import { API_BASE_URL } from "../config/api";

// Browser-side Web Push enrolment.
//
// Web-only: guarded on the APIs existing so importing this from shared code on
// native or an old TV browser is harmless. Tizen/webOS (Chromium 76) have no
// Push API, and iOS Safari only offers one for installed PWAs — both fall
// through to `supported === false` and the UI hides the control.

export type PushState = "unsupported" | "denied" | "off" | "on";

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function currentPushState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  const sub = await reg?.pushManager.getSubscription();
  return sub ? "on" : "off";
}

// The VAPID public key arrives base64url-encoded; PushManager wants raw bytes.
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Registers the worker, asks permission, subscribes, and hands the subscription
// to the backend. Throws with a human-readable message on the paths a parent can
// actually act on (permission blocked, push not configured server-side).
export async function enablePush(token: string): Promise<void> {
  if (!pushSupported()) {
    throw new Error("This browser doesn't support notifications.");
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      "Notifications are blocked for this site. Allow them in your browser settings and try again.",
    );
  }

  const keyRes = await fetch(`${API_BASE_URL}/v1/push/public-key`);
  const { publicKey } = (await keyRes.json()) as { publicKey: string | null };
  if (!publicKey) {
    throw new Error("Browser notifications aren't configured on the server yet.");
  }

  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  // Reuse an existing subscription when there is one — calling subscribe twice
  // with the same key returns the same endpoint, but being explicit avoids an
  // InvalidStateError if the stored key ever differs.
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      // Required to be true by every browser: silent pushes aren't allowed.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  const res = await fetch(`${API_BASE_URL}/v1/push/subscribe`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(sub.toJSON()),
  });
  if (!res.ok) {
    throw new Error("Couldn't register this browser for notifications.");
  }
}

// Unsubscribes locally and tells the backend to forget the endpoint, so a
// disabled browser stops receiving and stops being pushed to.
export async function disablePush(token: string): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  const { endpoint } = sub.toJSON();
  await sub.unsubscribe().catch(() => {});
  await fetch(`${API_BASE_URL}/v1/push/subscribe`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ endpoint }),
  }).catch(() => {
    // Local unsubscribe already happened; a stale row is pruned on the first
    // 410 from the push service.
  });
}
